import React, { useState, useEffect } from "react";
import { Commitment, Entity } from "@ifgengineering/client-invest-sdk";
import { theme } from "@ifgengineering/component-library";
import { ThemedText } from "@components/ThemedText";
import { EntityBadge } from "./EntityBadge.component";
import { getPortfolioEntityOptions } from "@components/portfolio/v2/helpers/filterInvestments";
import DownIcon from "@icon-park/react/es/icons/Down";
import {
  DropdownWrapper,
  DropdownButton,
  DropdownItem,
  DropdownItemsContainer,
} from "./styled";

export interface EntityBadgeDropdownProps {
  commitments: Commitment[];
  selectedEntity: string;
  onEntityChange: (entity: string) => void;
}

export const EntityBadgeDropdown: React.FC<EntityBadgeDropdownProps> = ({
  commitments,
  selectedEntity,
  onEntityChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const entityOptions = getPortfolioEntityOptions(commitments);
  const selectedOption = entityOptions.find(
    (opt) => opt.value === selectedEntity
  );

  useEffect(() => {
    const handleClickOutside = (e: any) => {
      if (ref.current && !ref?.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref]);

  const handleDropDownClick = () => setIsOpen(!isOpen);

  const handleEntitySelect = (entityValue: string) => {
    onEntityChange(entityValue);
    setIsOpen(false);
  };

  const getEntityFromCommitments = (entityId: string): Entity | null => {
    const commitment = commitments.find((c) => c.entityId === entityId);
    return (commitment?.entity as Entity) || null;
  };

  return (
    <DropdownWrapper ref={ref}>
      <DropdownButton
        data-testid="entity-badge-dropdown-toggle"
        onClick={handleDropDownClick}
        selectedOption={selectedOption?.value}
      >
        {selectedOption?.value === "all" ? (
          <ThemedText type="S16" color="SLATE700">
            {selectedOption.label}
          </ThemedText>
        ) : (
          <EntityBadge
            data-testid="entity-badge-dropdown-toggle"
            isIfIsa={selectedOption?.value === "IF-ISA"}
            entity={getEntityFromCommitments(selectedOption?.value || "")}
            hideInfo={true}
            displayNameConfig={{ hide: true }}
            iconConfig={{ size: 18, showLabel: true, labelSize: "S16" }}
          />
        )}
        <DownIcon fill={theme.colors.SLATE400} size={18} />
      </DropdownButton>
      {isOpen && (
        <DropdownItemsContainer menuAlignedFrom="left">
          {entityOptions.map((option) => {
            const entityBadge =
              option.value === "all" ? null : (
                <EntityBadge
                  isIfIsa={option.value === "IF-ISA"}
                  entity={getEntityFromCommitments(option.value)}
                  hideInfo={true}
                />
              );
            return (
              <DropdownItem
                key={option.value}
                onClick={() => handleEntitySelect(option.value)}
              >
                {option.value === "all" ? (
                  <ThemedText type="S16" color="SLATE700">
                    {option.label}
                  </ThemedText>
                ) : (
                  entityBadge
                )}
              </DropdownItem>
            );
          })}
        </DropdownItemsContainer>
      )}
    </DropdownWrapper>
  );
};
